// Left-side assembly / body tree: expand, hide/show, isolate, ghost.
import * as THREE from 'three';
import type { ModelTreeNode } from './model-builder';
import { loadTreeHeight, loadTreeWidth, saveTreeHeight, saveTreeWidth } from './preferences';
import { dom, makePanelDraggable } from './ui';
import { onThemeChange, requestRender, theme } from './viewer';

const GHOST_OPACITY = 0.28;
const GHOST_EDGE_OPACITY = 0.22;

let root: ModelTreeNode | null = null;
/** Lower-cased search text; empty = show the full tree. */
let query = '';
const collapsed = new Set<string>();
/** Bodies below each node, computed once per model (badges + header count). */
const bodyCounts = new Map<string, number>();
const ghostedIds = new Set<string>();
let isolatedId: string | null = null;
let selectedId: string | null = null;
/** Visibility snapshot before isolate, keyed by node id. */
let preIsolateVisibility: Map<string, boolean> | null = null;

/** The rows currently in the DOM, so a toggle can update them in place. */
interface TreeRow {
  node: ModelTreeNode;
  el: HTMLElement;
  iso: HTMLButtonElement;
  ghost: HTMLButtonElement;
  eye: HTMLButtonElement;
}
const rows = new Map<string, TreeRow>();
/** Which ids the current search shows; null = no search. */
let shownIds: Set<string> | null = null;

/** Effective visible/ghosted per node, refreshed in one pass before a sync. */
const rowState = new Map<string, { visible: boolean; ghosted: boolean }>();

/**
 * Fills `rowState` for the whole tree in a single post-order pass. Asking each
 * row on its own meant walking its subtree per row, which is O(n²) on a big
 * assembly; the answers for every row come out of one walk.
 */
function computeRowState(node: ModelTreeNode): { bodies: number; visibleBodies: number; ghostedBodies: number } {
  if (node.isBody || node.children.length === 0) {
    const vis = node.object.visible;
    const ghosted = ghostedIds.has(node.id);
    rowState.set(node.id, { visible: vis, ghosted });
    return {
      bodies: node.isBody ? 1 : 0,
      visibleBodies: node.isBody && vis ? 1 : 0,
      ghostedBodies: node.isBody && ghosted ? 1 : 0
    };
  }
  let bodies = 0;
  let visibleBodies = 0;
  let ghostedBodies = 0;
  for (const child of node.children) {
    const r = computeRowState(child);
    bodies += r.bodies;
    visibleBodies += r.visibleBodies;
    ghostedBodies += r.ghostedBodies;
  }
  // Open eye if any body below is visible; ghost only if every body below is.
  rowState.set(node.id, { visible: visibleBodies > 0, ghosted: bodies > 0 && ghostedBodies === bodies });
  return { bodies, visibleBodies, ghostedBodies };
}

/** Overlay meshes parented to bodies while a tree row is hovered. */
const hoverOverlays: THREE.Mesh[] = [];
let hoverNodeId: string | null = null;
const hoverMat = new THREE.MeshBasicMaterial({
  color: theme().accent,
  transparent: true,
  opacity: 0.38,
  depthTest: true,
  depthWrite: false,
  side: THREE.DoubleSide,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -1
});

function clearPartHoverHighlight(): void {
  for (const overlay of hoverOverlays) {
    overlay.parent?.remove(overlay);
  }
  hoverOverlays.length = 0;
  hoverNodeId = null;
  requestRender();
}

function highlightTreeNode(node: ModelTreeNode): void {
  if (hoverNodeId === node.id) return;
  clearPartHoverHighlight();
  hoverNodeId = node.id;
  hoverMat.color.set(theme().accent);
  walk(node, n => {
    if (!n.isBody) return;
    const mesh = n.object as THREE.Mesh;
    if (!mesh.visible || !mesh.geometry) return;
    // Walk up visibility — skip if an ancestor is hidden.
    let p: THREE.Object3D | null = mesh;
    while (p) {
      if (!p.visible) return;
      p = p.parent;
    }
    const overlay = new THREE.Mesh(mesh.geometry, hoverMat);
    overlay.renderOrder = 860;
    overlay.raycast = () => {};
    mesh.add(overlay);
    hoverOverlays.push(overlay);
  });
  requestRender();
}

function walk(node: ModelTreeNode, fn: (n: ModelTreeNode) => void): void {
  fn(node);
  for (const child of node.children) walk(child, fn);
}

function setSubtreeVisible(node: ModelTreeNode, visible: boolean): void {
  walk(node, n => {
    n.object.visible = visible;
  });
}

function cacheBodyCounts(node: ModelTreeNode): number {
  let n = node.isBody ? 1 : 0;
  for (const c of node.children) n += cacheBodyCounts(c);
  bodyCounts.set(node.id, n);
  return n;
}

function countBodies(node: ModelTreeNode): number {
  const cached = bodyCounts.get(node.id);
  if (cached !== undefined) return cached;
  let n = node.isBody ? 1 : 0;
  for (const c of node.children) n += countBodies(c);
  return n;
}

/** Bodies that are actually drawn: visible themselves and under no hidden parent. */
function countVisibleBodies(node: ModelTreeNode, parentVisible = true): number {
  const visible = parentVisible && node.object.visible;
  let n = node.isBody && visible ? 1 : 0;
  for (const c of node.children) n += countVisibleBodies(c, visible);
  return n;
}

function countGhostedBodies(node: ModelTreeNode): number {
  let n = 0;
  walk(node, x => {
    if (x.isBody && ghostedIds.has(x.id)) n++;
  });
  return n;
}

function countNodes(node: ModelTreeNode): number {
  let n = 1;
  for (const c of node.children) n += countNodes(c);
  return n;
}

/** Open eye if this body is visible, or any body under an assembly is visible. */
function effectiveVisible(node: ModelTreeNode): boolean {
  if (node.isBody || node.children.length === 0) return node.object.visible;
  let any = false;
  walk(node, n => {
    if (n.isBody && n.object.visible) any = true;
  });
  return any;
}

/** Ghost if this body is ghosted, or every body under an assembly is ghosted. */
function effectiveGhosted(node: ModelTreeNode): boolean {
  if (node.isBody || node.children.length === 0) return ghostedIds.has(node.id);
  let anyBody = false;
  let allGhosted = true;
  walk(node, n => {
    if (!n.isBody) return;
    anyBody = true;
    if (!ghostedIds.has(n.id)) allGhosted = false;
  });
  return anyBody && allGhosted;
}

function refreshGroupVisibility(node: ModelTreeNode): boolean {
  if (node.isBody || node.children.length === 0) return node.object.visible;
  const any = node.children.some(c => refreshGroupVisibility(c));
  node.object.visible = any;
  return any;
}

function makeAncestorsVisible(node: ModelTreeNode, targetId: string): boolean {
  if (node.id === targetId) return true;
  for (const child of node.children) {
    if (makeAncestorsVisible(child, targetId)) {
      node.object.visible = true;
      return true;
    }
  }
  return false;
}

function materialsOf(mesh: THREE.Mesh): THREE.Material[] {
  return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
}

/** Clone shared materials once so ghost opacity does not leak across bodies. */
function ensureOwnedMaterials(mesh: THREE.Mesh): THREE.Material[] {
  const owned = mesh.userData.ownedMaterials as THREE.Material[] | undefined;
  if (owned) return owned;

  // In x-ray / hidden-line mode the real material waits in userData.modeOriginal.
  const swappedOut = mesh.userData.modeOriginal as THREE.Material | THREE.Material[] | undefined;
  const source = swappedOut ? (Array.isArray(swappedOut) ? swappedOut : [swappedOut]) : materialsOf(mesh);
  const base = source.map(m => m.clone());
  mesh.userData.baseMaterials = source;
  mesh.userData.ownedMaterials = base;
  const assigned = base.length === 1 ? base[0] : base;
  if (swappedOut) mesh.userData.modeOriginal = assigned;
  else mesh.material = assigned;
  return base;
}

function applyGhostToMesh(mesh: THREE.Mesh, ghosted: boolean): void {
  const mats = ensureOwnedMaterials(mesh);
  for (const mat of mats) {
    mat.transparent = ghosted;
    mat.opacity = ghosted ? GHOST_OPACITY : 1;
    mat.depthWrite = !ghosted;
    mat.needsUpdate = true;
  }
  mesh.renderOrder = ghosted ? 1 : 0;
}

function syncEdgeOpacity(mesh: THREE.Mesh, ghosted: boolean): void {
  for (const child of mesh.children) {
    if (!child.userData.isShadedEdge) continue;
    const lineMat = (child as THREE.LineSegments).material as THREE.Material & { opacity: number };
    lineMat.transparent = true;
    // Match display.ts defaults when opaque; quieter when ghosted.
    const dark = document.body.classList.contains('dark');
    lineMat.opacity = ghosted ? GHOST_EDGE_OPACITY : dark ? 0.85 : 0.72;
    lineMat.needsUpdate = true;
  }
}

function setSubtreeGhost(node: ModelTreeNode, ghosted: boolean): void {
  walk(node, n => {
    if (ghosted) ghostedIds.add(n.id);
    else ghostedIds.delete(n.id);
    if (!n.isBody) return;
    const mesh = n.object as THREE.Mesh;
    applyGhostToMesh(mesh, ghosted);
    syncEdgeOpacity(mesh, ghosted);
  });
}

/** The one reset in the header: visibility, isolation and ghosting together. */
function showAll(): void {
  if (!root) return;
  isolatedId = null;
  preIsolateVisibility = null;
  setSubtreeVisible(root, true);
  setSubtreeGhost(root, false);
  syncRows();
  requestRender();
}

function isolate(node: ModelTreeNode): void {
  if (!root) return;
  if (isolatedId === node.id) {
    if (preIsolateVisibility) {
      walk(root, n => {
        const v = preIsolateVisibility!.get(n.id);
        if (v !== undefined) n.object.visible = v;
      });
    } else {
      setSubtreeVisible(root, true);
    }
    isolatedId = null;
    preIsolateVisibility = null;
  } else {
    preIsolateVisibility = new Map();
    walk(root, n => preIsolateVisibility!.set(n.id, n.object.visible));
    setSubtreeVisible(root, false);
    setSubtreeVisible(node, true);
    makeAncestorsVisible(root, node.id);
    isolatedId = node.id;
  }
  syncRows();
  requestRender();
}

function toggleVisibility(node: ModelTreeNode): void {
  if (isolatedId) {
    isolatedId = null;
    preIsolateVisibility = null;
  }
  const next = !effectiveVisible(node);
  setSubtreeVisible(node, next);
  if (root) refreshGroupVisibility(root);
  syncRows();
  requestRender();
}

function toggleGhost(node: ModelTreeNode): void {
  const next = !effectiveGhosted(node);
  setSubtreeGhost(node, next);
  syncRows();
  requestRender();
}

// Tabler Icons (MIT)
const ICON_CHEVRON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6l6 -6" /></svg>';
const ICON_ASSEMBLY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4l-8 4l8 4l8 -4l-8 -4" /> <path d="M4 12l8 4l8 -4" /> <path d="M4 16l8 4l8 -4" /></svg>';
const ICON_PART = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5" /> <path d="M12 12l8 -4.5" /> <path d="M12 12l0 9" /> <path d="M12 12l-8 -4.5" /></svg>';
const ICON_GHOST = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 9h.01" /> <path d="M14 9h.01" /> <path d="M12 3a7 7 0 0 1 7 7v1l1 0a2 2 0 1 1 0 4l-1 0v3l2 3h-10a6 6 0 0 1 -6 -5.775l0 -.226l-1 0a2 2 0 0 1 0 -4l1 0v-1a7 7 0 0 1 7 -7z" /> <path d="M11 14h2a1 1 0 0 0 -2 0z" /></svg>';
const ICON_EYE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" /> <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" /></svg>';
const ICON_ISOLATE = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r=".5" fill="currentColor" /> <path d="M12 12m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" /> <path d="M12 3l0 2" /> <path d="M3 12l2 0" /> <path d="M12 19l0 2" /> <path d="M19 12l2 0" /></svg>';
const ICON_EYE_OFF = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" /> <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" /> <path d="M3 3l18 18" /></svg>';

/** Nodes to render for the current search: matches plus their ancestors. */
function matchingIds(node: ModelTreeNode, out: Set<string>): boolean {
  let keep = node.name.toLowerCase().includes(query);
  for (const child of node.children) {
    if (matchingIds(child, out)) keep = true;
  }
  if (keep) out.add(node.id);
  return keep;
}

function renderNode(
  node: ModelTreeNode,
  depth: number,
  into: HTMLElement,
  shown: Set<string> | null
): void {
  if (shown && !shown.has(node.id)) return;
  const hasKids = node.children.length > 0;
  // While searching, every matching branch is open.
  const isCollapsed = !shown && collapsed.has(node.id);

  const row = document.createElement('div');
  row.className = 'mt-row' + (node.isBody ? '' : ' asm');
  row.style.setProperty('--depth', String(depth));
  row.dataset.id = node.id;
  row.setAttribute('role', 'treeitem');
  if (hasKids) row.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');

  // Indent guides: one hairline per ancestor level.
  for (let i = 0; i < depth; i++) {
    const guide = document.createElement('span');
    guide.className = 'mt-guide';
    guide.style.setProperty('--i', String(i));
    row.appendChild(guide);
  }

  const twist = document.createElement('button');
  twist.type = 'button';
  twist.className = 'mt-twist' + (hasKids ? '' : ' empty') + (isCollapsed ? ' closed' : '');
  twist.tabIndex = hasKids ? 0 : -1;
  twist.setAttribute('aria-label', isCollapsed ? 'Expand' : 'Collapse');
  if (hasKids) {
    twist.innerHTML = ICON_CHEVRON;
    twist.addEventListener('click', e => {
      e.stopPropagation();
      if (collapsed.has(node.id)) collapsed.delete(node.id);
      else collapsed.add(node.id);
      renderList();
    });
  }

  const icon = document.createElement('span');
  icon.className = 'mt-icon';
  icon.innerHTML = node.isBody ? ICON_PART : ICON_ASSEMBLY;

  const label = document.createElement('button');
  label.type = 'button';
  label.className = 'mt-label';
  label.textContent = node.name;
  label.title = `${node.name}\nDouble-click to isolate`;
  label.addEventListener('click', () => {
    selectedId = node.id;
    dom.modelTreeList.querySelectorAll('.mt-row.selected').forEach(el => el.classList.remove('selected'));
    row.classList.add('selected');
  });
  label.addEventListener('dblclick', e => {
    e.preventDefault();
    isolate(node);
  });

  const parts: HTMLElement[] = [twist, icon, label];
  if (hasKids) {
    const count = document.createElement('span');
    count.className = 'mt-count';
    const n = countBodies(node);
    count.textContent = String(n);
    count.title = `${n} part${n === 1 ? '' : 's'}`;
    parts.push(count);
  }

  const iso = document.createElement('button');
  iso.type = 'button';
  iso.className = 'mt-act mt-iso';
  iso.innerHTML = ICON_ISOLATE;
  iso.addEventListener('click', e => {
    e.stopPropagation();
    isolate(node);
  });

  const ghost = document.createElement('button');
  ghost.type = 'button';
  ghost.className = 'mt-act mt-ghost';
  ghost.innerHTML = ICON_GHOST;
  ghost.addEventListener('click', e => {
    e.stopPropagation();
    toggleGhost(node);
  });

  const eye = document.createElement('button');
  eye.type = 'button';
  eye.className = 'mt-act mt-eye';
  eye.addEventListener('click', e => {
    e.stopPropagation();
    toggleVisibility(node);
  });
  parts.push(iso, ghost, eye);

  row.addEventListener('pointerenter', () => highlightTreeNode(node));
  row.addEventListener('pointerleave', () => {
    if (hoverNodeId === node.id) clearPartHoverHighlight();
  });

  row.append(...parts);
  const entry: TreeRow = { node, el: row, iso, ghost, eye };
  rows.set(node.id, entry);
  applyRowState(entry);
  into.appendChild(row);

  if (hasKids && !isCollapsed) {
    for (const child of node.children) renderNode(child, depth + 1, into, shown);
  }
}

/** Writes one row's visible / ghosted / isolated / selected state onto its DOM. */
function applyRowState(r: TreeRow): void {
  const st = rowState.get(r.node.id);
  const visible = st ? st.visible : effectiveVisible(r.node);
  const ghosted = st ? st.ghosted : effectiveGhosted(r.node);
  const isolated = isolatedId === r.node.id;

  r.el.classList.toggle('isolated', isolated);
  r.el.classList.toggle('selected', selectedId === r.node.id);
  r.el.classList.toggle('hidden-part', !visible);
  r.el.classList.toggle('ghosted-part', ghosted);

  r.iso.classList.toggle('on', isolated);
  r.iso.title = isolated ? 'Show everything again' : 'Isolate (show only this)';
  r.iso.setAttribute('aria-label', r.iso.title);
  r.iso.setAttribute('aria-pressed', isolated ? 'true' : 'false');

  r.ghost.classList.toggle('on', ghosted);
  r.ghost.title = ghosted ? 'Make solid' : 'Ghost';
  r.ghost.setAttribute('aria-label', r.ghost.title);
  r.ghost.setAttribute('aria-pressed', ghosted ? 'true' : 'false');

  r.eye.classList.toggle('on', !visible);
  r.eye.title = visible ? 'Hide' : 'Show';
  r.eye.setAttribute('aria-label', r.eye.title);
  const wantEyeOff = !visible;
  if (r.eye.dataset.off !== String(wantEyeOff)) {
    r.eye.innerHTML = visible ? ICON_EYE : ICON_EYE_OFF;
    r.eye.dataset.off = String(wantEyeOff);
  }
}

/**
 * Hiding, ghosting or isolating does not change which rows exist - only what
 * each one says. Rebuilding the whole list for that threw away every row and
 * its listeners on each click, which is what made large assemblies crawl.
 */
function syncRows(): void {
  if (!root) return;
  computeRowState(root);
  for (const r of rows.values()) applyRowState(r);
  renderHeaderMeta(shownIds);

  // The hovered row keeps its overlay, but the body under it may just have
  // been hidden - re-run the highlight for what is there now.
  const hovered = hoverNodeId ? rows.get(hoverNodeId)?.node : null;
  if (hovered) {
    clearPartHoverHighlight();
    highlightTreeNode(hovered);
  }
}

function renderList(): void {
  clearPartHoverHighlight();
  const list = dom.modelTreeList;
  rows.clear();
  list.textContent = '';
  if (!root) return;
  let shown: Set<string> | null = null;
  if (query) {
    shown = new Set<string>();
    matchingIds(root, shown);
  }
  shownIds = shown;
  computeRowState(root);
  renderNode(root, 0, list, shown);
  dom.modelTreeEmpty.classList.toggle('hidden', !shown || shown.size > 0);
  renderHeaderMeta(shown);
}

/**
 * The header used to read `4 parts` even with one body isolated, so the tree
 * said nothing about the state it was in. It now names whatever is narrowing
 * the view, and the show-all button lights up while there is one.
 */
function renderHeaderMeta(shown: Set<string> | null): void {
  if (!root) return;
  const total = countBodies(root);
  const visible = countVisibleBodies(root);
  const ghosted = countGhostedBodies(root);
  const parts: string[] = [];

  if (shown) {
    let matches = 0;
    walk(root, n => {
      if (n.isBody && shown.has(n.id)) matches++;
    });
    parts.push(`${matches} of ${total} match`);
  } else if (visible < total) {
    parts.push(`${visible} of ${total} shown`);
  } else {
    parts.push(`${total} part${total === 1 ? '' : 's'}`);
  }
  if (ghosted > 0) parts.push(`${ghosted} ghosted`);

  dom.modelTreeMeta.textContent = parts.join(' · ');
  const narrowed = visible < total || ghosted > 0;
  dom.modelTreeShowAll.classList.toggle('attention', narrowed);
  dom.modelTreeShowAll.title = narrowed
    ? 'Show all parts — clears hidden, isolated and ghosted'
    : 'Show all parts';
}

export function setModelTree(tree: ModelTreeNode | null): void {
  clearPartHoverHighlight();
  root = tree;
  rows.clear();
  rowState.clear();
  shownIds = null;
  bodyCounts.clear();
  if (tree) cacheBodyCounts(tree);
  query = '';
  dom.modelTreeSearch.value = '';
  dom.modelTreeSearchClear.classList.add('hidden');
  collapsed.clear();
  ghostedIds.clear();
  isolatedId = null;
  selectedId = null;
  preIsolateVisibility = null;
  if (tree) {
    // Collapse heavy nested assemblies so large models stay scannable.
    walk(tree, n => {
      if (n.id === tree.id) return;
      if (n.children.length > 0 && countNodes(n) > 36) {
        for (const c of n.children) {
          if (c.children.length > 0) collapsed.add(c.id);
        }
      }
    });
    // The tree covers part of the model, and most of the time you are looking
    // at the model - so it starts closed and is opened from the toolbar, at
    // every width.
    dom.modelTree.classList.add('hidden');
  } else {
    dom.modelTree.classList.add('hidden');
  }
  syncTreeButton();
  renderList();
}

export function hasModelTree(): boolean {
  return root !== null;
}

export function isTreeOpen(): boolean {
  return root !== null && !dom.modelTree.classList.contains('hidden');
}

export function setTreeOpen(open: boolean): void {
  if (root === null) return;
  dom.modelTree.classList.toggle('hidden', !open);
  syncTreeButton();
}

export function toggleTreeOpen(): void {
  setTreeOpen(!isTreeOpen());
}

function syncTreeButton(): void {
  const open = isTreeOpen();
  dom.treeBtn.setAttribute('aria-pressed', open ? 'true' : 'false');
  dom.treeBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  dom.treeBtn.classList.toggle('active', open);
}

/** Path of node ids from the root to the node whose object is `target`. */
function pathTo(node: ModelTreeNode, target: THREE.Object3D, path: ModelTreeNode[]): boolean {
  path.push(node);
  if (node.object === target) return true;
  for (const child of node.children) {
    if (pathTo(child, target, path)) return true;
  }
  path.pop();
  return false;
}

/**
 * Select the tree row for a body picked in the 3D view (null clears): opens
 * its parent assemblies, scrolls it into view and briefly flashes the body.
 */
export function selectTreeObject(target: THREE.Object3D | null): void {
  if (!root) return;
  if (!target) {
    if (selectedId === null) return;
    selectedId = null;
    syncRows();
    return;
  }
  const path: ModelTreeNode[] = [];
  if (!pathTo(root, target, path)) return;
  const node = path[path.length - 1];
  for (const n of path.slice(0, -1)) collapsed.delete(n.id);
  if (query && !node.name.toLowerCase().includes(query)) {
    query = '';
    dom.modelTreeSearch.value = '';
    dom.modelTreeSearchClear.classList.add('hidden');
  }
  selectedId = node.id;
  renderList();
  const row = dom.modelTreeList.querySelector<HTMLElement>(`.mt-row[data-id="${CSS.escape(node.id)}"]`);
  row?.scrollIntoView({ block: 'nearest' });
  highlightTreeNode(node);
  window.setTimeout(() => {
    if (hoverNodeId === node.id) clearPartHoverHighlight();
  }, 700);
}

export function clearModelTree(): void {
  setModelTree(null);
}

/** Re-apply ghost materials after shaded edges are rebuilt. */
export function refreshGhostMaterials(): void {
  if (!root || ghostedIds.size === 0) return;
  walk(root, n => {
    if (!n.isBody || !ghostedIds.has(n.id)) return;
    const mesh = n.object as THREE.Mesh;
    applyGhostToMesh(mesh, true);
    syncEdgeOpacity(mesh, true);
  });
}

const TREE_MIN_WIDTH = 220;
const TREE_MIN_HEIGHT = 160;

function treeMaxWidth(): number {
  return Math.max(TREE_MIN_WIDTH, Math.min(720, window.innerWidth * 0.6));
}

/** Down to just above the window bottom (the tree may cover the XYZ gizmo). */
function treeMaxHeight(): number {
  const top = dom.modelTree.getBoundingClientRect().top || 72;
  return Math.max(TREE_MIN_HEIGHT, window.innerHeight - top - 16);
}

function applyTreeWidth(width: number | null): void {
  if (width === null) dom.modelTree.style.removeProperty('--tree-width');
  else dom.modelTree.style.setProperty('--tree-width', `${Math.round(width)}px`);
}

function applyTreeHeight(height: number | null): void {
  dom.modelTree.classList.toggle('has-height', height !== null);
  if (height === null) dom.modelTree.style.removeProperty('--tree-height');
  else dom.modelTree.style.setProperty('--tree-height', `${Math.round(height)}px`);
}

/**
 * Drag the right edge (width), bottom edge (height) or bottom-right corner
 * (both). Sizes are remembered; double-click a handle to reset its axes.
 */
function initTreeResize(): void {
  const savedW = loadTreeWidth();
  if (savedW !== null) applyTreeWidth(Math.min(savedW, treeMaxWidth()));
  const savedH = loadTreeHeight();
  if (savedH !== null) applyTreeHeight(savedH);

  let axis: string | null = null;
  let startX = 0, startY = 0, startW = 0, startH = 0, scale = 1;
  let width: number | null = null;
  let height: number | null = null;

  dom.modelTree.querySelectorAll<HTMLElement>('.model-tree-resize').forEach(handle => {
    const handleAxis = handle.dataset.axis ?? 'x';
    handle.addEventListener('pointerdown', e => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = dom.modelTree.getBoundingClientRect();
      // The panel is zoomed by --chrome-scale on large screens.
      scale = rect.width / Math.max(dom.modelTree.offsetWidth, 1);
      axis = handleAxis;
      startX = e.clientX;
      startY = e.clientY;
      startW = dom.modelTree.offsetWidth;
      startH = dom.modelTree.offsetHeight;
      width = null;
      height = null;
      dom.modelTree.classList.add('resizing');
      dom.modelTree.dataset.resizeAxis = axis;
      try { handle.setPointerCapture(e.pointerId); } catch { /* window listeners still track the drag */ }
    });
    handle.addEventListener('dblclick', () => {
      if (handleAxis.includes('x')) { applyTreeWidth(null); saveTreeWidth(null); }
      if (handleAxis.includes('y')) { applyTreeHeight(null); saveTreeHeight(null); }
    });
  });

  // Tracked on window so the drag keeps working when the pointer leaves the thin handle.
  window.addEventListener('pointermove', e => {
    if (!axis) return;
    if (axis.includes('x')) {
      width = Math.min(Math.max(startW + (e.clientX - startX) / scale, TREE_MIN_WIDTH), treeMaxWidth());
      applyTreeWidth(width);
    }
    if (axis.includes('y')) {
      height = Math.min(Math.max(startH + (e.clientY - startY) / scale, TREE_MIN_HEIGHT), treeMaxHeight());
      applyTreeHeight(height);
    }
  });
  const end = (): void => {
    if (!axis) return;
    axis = null;
    dom.modelTree.classList.remove('resizing');
    delete dom.modelTree.dataset.resizeAxis;
    if (width !== null) saveTreeWidth(width);
    if (height !== null) saveTreeHeight(height);
  };
  window.addEventListener('pointerup', end);
  window.addEventListener('pointercancel', end);
  window.addEventListener('resize', () => {
    const w = loadTreeWidth();
    if (w !== null) applyTreeWidth(Math.min(w, treeMaxWidth()));
  });
}

/**
 * Hang the tree under the title block instead of over it. The model's size and
 * the unit it is being read in sit there, and those are exactly the numbers you
 * open the tree to check against - so measure the block rather than guess an
 * offset, because it grows when a unit hint appears or the window narrows.
 *
 * The value is divided by the panel's own zoom: with `zoom`, a top of T px
 * renders at T x zoom from the edge.
 */
function syncTreeTop(): void {
  const bottom = dom.titleBlock.getBoundingClientRect().bottom;
  const zoom = Number.parseFloat(getComputedStyle(dom.modelTree).zoom) || 1;
  const top = Math.max(72, Math.round(bottom) + 10) / zoom;
  document.documentElement.style.setProperty('--tree-top', `${Math.round(top)}px`);
}

export function initModelTree(): void {
  initTreeResize();
  syncTreeTop();
  new ResizeObserver(syncTreeTop).observe(dom.titleBlock);
  window.addEventListener('resize', syncTreeTop);
  makePanelDraggable(dom.modelTree, dom.modelTreeHeader);
  // Rotating the device or resizing the window used to open or close the tree
  // for you. It no longer does: the tree is yours either way, and the toolbar
  // button is there at every width to get it back.
  dom.modelTreeShowAll.addEventListener('click', showAll);
  dom.modelTreeClose.addEventListener('click', () => setTreeOpen(false));
  dom.modelTreeList.addEventListener('pointerleave', () => clearPartHoverHighlight());

  const applySearch = (): void => {
    query = dom.modelTreeSearch.value.trim().toLowerCase();
    dom.modelTreeSearchClear.classList.toggle('hidden', !dom.modelTreeSearch.value);
    renderList();
  };
  dom.modelTreeSearch.addEventListener('input', applySearch);
  dom.modelTreeSearch.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    // Esc clears the search first instead of closing every panel.
    if (dom.modelTreeSearch.value) {
      e.stopPropagation();
      dom.modelTreeSearch.value = '';
      applySearch();
    } else {
      dom.modelTreeSearch.blur();
    }
  });
  dom.modelTreeSearchClear.addEventListener('click', () => {
    dom.modelTreeSearch.value = '';
    applySearch();
    dom.modelTreeSearch.focus();
  });
  onThemeChange(() => {
    hoverMat.color.set(theme().accent);
    if (root) {
      walk(root, n => {
        if (!n.isBody) return;
        syncEdgeOpacity(n.object as THREE.Mesh, ghostedIds.has(n.id));
      });
    }
    if (hoverOverlays.length) requestRender();
  });
}
